import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { ActiveSession } from '@/hooks/useDashboardData';

interface DecisionAreasChartProps {
  sessions: ActiveSession[];
}

const AREA_LABELS: Record<string, string> = {
  strategy: 'Strategie',
  product: 'Produkt',
  growth: 'Wachstum',
  team: 'Team',
  operations: 'Operations',
  finance: 'Finanzen',
};

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
];

export const DecisionAreasChart = ({ sessions }: DecisionAreasChartProps) => {
  const chartData = useMemo(() => {
    const areaCounts: Record<string, number> = {};

    sessions.forEach((session) => {
      if (session.decision_type) {
        areaCounts[session.decision_type] = (areaCounts[session.decision_type] || 0) + 1;
      }
    });

    return Object.entries(areaCounts)
      .map(([area, count]) => ({
        name: AREA_LABELS[area] || area,
        value: count,
      }))
      .sort((a, b) => b.value - a.value);
  }, [sessions]);

  if (sessions.length === 0) {
    return (
      <div className="h-[250px] flex items-center justify-center text-sm text-muted-foreground">
        Noch keine Daten verfügbar
      </div>
    );
  }

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
        <Pie
          data={chartData}
          cx="50%"
          cy="45%"
          innerRadius={50}
          outerRadius={85}
          paddingAngle={2}
          dataKey="value"
          stroke="hsl(var(--background))"
          strokeWidth={2}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip 
          contentStyle={{
            backgroundColor: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '8px',
            color: 'hsl(var(--popover-foreground))',
            fontSize: '13px',
          }}
          formatter={(value: number, name: string) => [
            `${value} (${Math.round((value / total) * 100)}%)`,
            name
          ]}
        />
        <Legend 
          verticalAlign="bottom"
          align="center"
          layout="horizontal"
          iconType="circle"
          iconSize={10}
          wrapperStyle={{
            paddingTop: '16px',
          }}
          formatter={(value) => (
            <span style={{ color: 'hsl(var(--foreground))', fontSize: '12px', marginRight: '8px' }}>
              {value}
            </span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
